import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Box, CircularProgress } from '@mui/material';
import 'react-toastify/dist/ReactToastify.css';

// Layout components — eagerly loaded (needed for initial render)
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';

// Auth pages — eagerly loaded (needed before any other page can render)
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';

// Protected Route components — eagerly loaded (guard logic)
import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleBasedRoute from './components/auth/RoleBasedRoute';

// Page loading fallback
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress />
  </Box>
);

// Dashboard pages — lazy loaded
const Dashboard = React.lazy(() => import('./pages/dashboard/Dashboard'));
const OrgManagementDashboard = React.lazy(() => import('./pages/dashboard/OrgManagementDashboard'));
const DirectorsDashboard = React.lazy(() => import('./pages/dashboard/DirectorsDashboard'));
const PrincipalDashboard = React.lazy(() => import('./pages/dashboard/PrincipalDashboard'));
const AdminDashboard = React.lazy(() => import('./pages/dashboard/AdminDashboard'));
const DeanDashboard = React.lazy(() => import('./pages/dashboard/DeanDashboard'));
const HrManagerDashboard = React.lazy(() => import('./pages/dashboard/HrManagerDashboard'));
const StudentMentorsDashboard = React.lazy(() => import('./pages/dashboard/StudentMentorsDashboard'));
const StudentCoordinatorsDashboard = React.lazy(() => import('./pages/dashboard/StudentCoordinatorsDashboard'));
const TeachersDashboard = React.lazy(() => import('./pages/dashboard/TeachersDashboard'));

// Student pages
const StudentList = React.lazy(() => import('./pages/students/StudentList'));
const StudentDetails = React.lazy(() => import('./pages/students/StudentDetails'));
const StudentForm = React.lazy(() => import('./pages/students/StudentForm'));
const StudentAnalytics = React.lazy(() => import('./pages/students/StudentAnalytics'));
const AlumniList = React.lazy(() => import('./pages/students/AlumniList'));
const AcademicModule = React.lazy(() => import('./pages/academic/AcademicModule'));
const AttendanceModule = React.lazy(() => import('./pages/attendance/AttendanceModule'));
const ExaminationModule = React.lazy(() => import('./pages/examination/ExaminationModule'));

// Attendance pages
const AttendanceList = React.lazy(() => import('./pages/attendance/AttendanceList'));
const MarkAttendance = React.lazy(() => import('./pages/attendance/MarkAttendance'));
const SubjectAttendance = React.lazy(() => import('./pages/attendance/SubjectAttendance'));
const TimetableAttendance = React.lazy(() => import('./pages/attendance/TimetableAttendance'));

// Grade pages
const GradeList = React.lazy(() => import('./pages/grades/GradeList'));
const GradeForm = React.lazy(() => import('./pages/grades/GradeForm'));

// Class pages
const ClassList = React.lazy(() => import('./pages/classes/ClassList'));
const ClassDetails = React.lazy(() => import('./pages/classes/ClassDetails'));
const ClassForm = React.lazy(() => import('./pages/classes/ClassForm'));

// Course pages
const CourseList = React.lazy(() => import('./pages/courses/CourseList'));
const CourseDetails = React.lazy(() => import('./pages/courses/CourseDetails'));
const CourseForm = React.lazy(() => import('./pages/courses/CourseForm'));

// Admission pages
const AdmissionList = React.lazy(() => import('./pages/admissions/AdmissionList'));
const AdmissionForm = React.lazy(() => import('./pages/admissions/AdmissionForm'));
const AdmissionDetails = React.lazy(() => import('./pages/admissions/AdmissionDetails'));
const AdmissionCriteriaList = React.lazy(() => import('./pages/admissions/AdmissionCriteriaList'));
const AdmissionCriteriaForm = React.lazy(() => import('./pages/admissions/AdmissionCriteriaForm'));

// Finance pages — new enterprise finance module
const FinanceDashboard = React.lazy(() => import('./pages/finance/FinanceDashboard'));
const ChartOfAccounts = React.lazy(() => import('./pages/finance/ChartOfAccounts'));
const GeneralLedger = React.lazy(() => import('./pages/finance/GeneralLedger'));
const Receivables = React.lazy(() => import('./pages/finance/Receivables'));
const Payables = React.lazy(() => import('./pages/finance/Payables'));
const CashBank = React.lazy(() => import('./pages/finance/CashBank'));
const Budgets = React.lazy(() => import('./pages/finance/Budgets'));
const Expenses = React.lazy(() => import('./pages/finance/Expenses'));
const Income = React.lazy(() => import('./pages/finance/Income'));
const Statements = React.lazy(() => import('./pages/finance/Statements'));
const Reports = React.lazy(() => import('./pages/finance/Reports'));
const Approvals = React.lazy(() => import('./pages/finance/Approvals'));
const TaxCompliance = React.lazy(() => import('./pages/finance/TaxCompliance'));
const AuditLogs = React.lazy(() => import('./pages/finance/AuditLogs'));
const AIInsights = React.lazy(() => import('./pages/finance/AIInsights'));
const Integrations = React.lazy(() => import('./pages/finance/Integrations'));
const Procurement = React.lazy(() => import('./pages/finance/Procurement'));
const FixedAssets = React.lazy(() => import('./pages/finance/FixedAssets'));

// HR & Payroll Module
const HRModule = React.lazy(() => import('./pages/hr/HRModule'));

// Fee Management page
const FeeManagement = React.lazy(() => import('./pages/fees/FeeManagement'));

// Hostel page
const HostelManagement = React.lazy(() => import('./pages/hostel/HostelManagement'));

// Cafeteria page
const CafeteriaManagement = React.lazy(() => import('./pages/cafeteria/CafeteriaManagement'));

// Houses & Clubs page
const HousesClubs = React.lazy(() => import('./pages/houses/HousesClubs'));

// Payroll page
const PayrollManagement = React.lazy(() => import('./pages/payroll/PayrollManagement'));

// ICSE Exam page
const ExamICSE = React.lazy(() => import('./pages/exams/ExamICSE'));

// Communication page
const CommunicationManagement = React.lazy(() => import('./pages/communication/CommunicationManagement'));

// Inventory & Store page
const InventoryStore = React.lazy(() => import('./pages/inventory/InventoryStore'));

// Management Dashboard page
const ManagementDashboard = React.lazy(() => import('./pages/dashboard/ManagementDashboard'));

// Reports Module page
const ReportsModule = React.lazy(() => import('./pages/reports/ReportsModule'));

// Transport pages
const TransportManagement = React.lazy(() => import('./pages/transport/TransportManagement'));
const VehicleTracking = React.lazy(() => import('./pages/transport/VehicleTracking'));
const TransportReports = React.lazy(() => import('./pages/transport/TransportReports'));
const TrackingSettings = React.lazy(() => import('./pages/transport/TrackingSettings'));

// Staff pages
const StaffManagement = React.lazy(() => import('./pages/staff/StaffManagement'));
const BiometricEnrollment = React.lazy(() => import('./pages/staff/BiometricEnrollment'));

// HR pages
const HRDashboard = React.lazy(() => import('./pages/hr/HRDashboard'));
const OnboardingManagement = React.lazy(() => import('./pages/hr/OnboardingManagement'));
const OffboardingManagement = React.lazy(() => import('./pages/hr/OffboardingManagement'));
const InterviewManagement = React.lazy(() => import('./pages/hr/InterviewManagement'));
const LeaveManagement = React.lazy(() => import('./pages/hr/LeaveManagement'));
const SelfServicePortal = React.lazy(() => import('./pages/hr/SelfServicePortal'));
const HRAnalyticsDashboard = React.lazy(() => import('./pages/hr/HRAnalyticsDashboard'));

// Asset Management pages
const AssetManagement = React.lazy(() => import('./pages/assets/AssetManagement'));
const AssetList = React.lazy(() => import('./pages/assets/AssetList'));
const AssetForm = React.lazy(() => import('./pages/assets/AssetForm'));
const AssetDetails = React.lazy(() => import('./pages/assets/AssetDetails'));
const AssetCategories = React.lazy(() => import('./pages/assets/AssetCategories'));
const AssetMaintenance = React.lazy(() => import('./pages/assets/AssetMaintenance'));
const AssetTransfers = React.lazy(() => import('./pages/assets/AssetTransfers'));
const AssetDisposals = React.lazy(() => import('./pages/assets/AssetDisposals'));
const StaffAssetRequests = React.lazy(() => import('./pages/assets/StaffAssetRequests'));

// Library Management pages
const LibraryManagement = React.lazy(() => import('./pages/library/LibraryManagement'));
const Books = React.lazy(() => import('./pages/library/Books'));
const BookForm = React.lazy(() => import('./pages/library/BookForm'));
const LibraryMembers = React.lazy(() => import('./pages/library/LibraryMembers'));
const BookIssues = React.lazy(() => import('./pages/library/BookIssues'));
const BookReservations = React.lazy(() => import('./pages/library/BookReservations'));
const Fines = React.lazy(() => import('./pages/library/Fines'));
const MemberForm = React.lazy(() => import('./pages/library/MemberForm'));

// Front Office Management pages
const FrontOfficeManagement = React.lazy(() => import('./pages/frontoffice/FrontOfficeManagement'));
const Visitors = React.lazy(() => import('./pages/frontoffice/Visitors'));
const Appointments = React.lazy(() => import('./pages/frontoffice/Appointments'));
const Inquiries = React.lazy(() => import('./pages/frontoffice/Inquiries'));
const PhoneCalls = React.lazy(() => import('./pages/frontoffice/PhoneCalls'));
const MailPackages = React.lazy(() => import('./pages/frontoffice/MailPackages'));

// Facility Management pages
const FacilityManagement = React.lazy(() => import('./pages/facility/FacilityManagement'));
const Facilities = React.lazy(() => import('./pages/facility/Facilities'));
const FacilityBookings = React.lazy(() => import('./pages/facility/FacilityBookings'));
const FacilityMaintenance = React.lazy(() => import('./pages/facility/FacilityMaintenance'));
const FacilityInventory = React.lazy(() => import('./pages/facility/FacilityInventory'));
const FacilityInspections = React.lazy(() => import('./pages/facility/FacilityInspections'));

// Board and Affiliation pages
const BoardManagement = React.lazy(() => import('./pages/boards/BoardManagement'));
const AffiliationManagement = React.lazy(() => import('./pages/affiliations/AffiliationManagement'));
const Scholarships = React.lazy(() => import('./pages/boards/Scholarships'));
const ExamSchedules = React.lazy(() => import('./pages/boards/ExamSchedules'));
const GradingScales = React.lazy(() => import('./pages/boards/GradingScales'));
const ServiceBooks = React.lazy(() => import('./pages/boards/ServiceBooks'));
const RTEReimbursements = React.lazy(() => import('./pages/boards/RTEReimbursements'));
const BoardExamRegistrations = React.lazy(() => import('./pages/boards/BoardExamRegistrations'));
const ElectiveChoices = React.lazy(() => import('./pages/boards/ElectiveChoices'));
const SubjectGroups = React.lazy(() => import('./pages/boards/SubjectGroups'));
const UDISEExports = React.lazy(() => import('./pages/boards/UDISEExports'));

// School Master Setup page
const SchoolMasterSetup = React.lazy(() => import('./pages/setup/SchoolMasterSetup'));

// User Management (unified) page
const UserManagementUnified = React.lazy(() => import('./pages/users/UserManagement'));

// Admission Module (unified) page
const AdmissionModule = React.lazy(() => import('./pages/admissions/AdmissionModule'));
const AdmissionWorkflow = React.lazy(() => import('./pages/admissions/AdmissionWorkflow'));

// Admin pages
const UserManagement = React.lazy(() => import('./pages/admin/UserManagement'));
const BranchManagement = React.lazy(() => import('./pages/admin/BranchManagement'));
const Settings = React.lazy(() => import('./pages/admin/Settings'));
const SchoolProfilePage = React.lazy(() => import('./pages/admin/SchoolProfile'));
const AdminAuditLogs = React.lazy(() => import('./pages/admin/AuditLogs'));
const UserRoles = React.lazy(() => import('./pages/admin/UserRoles'));
const SystemHealth = React.lazy(() => import('./pages/admin/SystemHealth'));

// Profile & Notifications pages
const Profile = React.lazy(() => import('./pages/profile/Profile'));
const Notifications = React.lazy(() => import('./pages/notifications/Notifications'));

function App() {
  return (
    <>
      <Routes>
        {/* Auth routes */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
        </Route>

        {/* Protected routes */}
        <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Suspense fallback={<PageLoader />}><Dashboard /></Suspense>} />

          {/* Role-based dashboards */}
          <Route path="dashboard/org-management" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['organisation_management']}><OrgManagementDashboard /></RoleBasedRoute></Suspense>} />
          <Route path="dashboard/directors" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['directors']}><DirectorsDashboard /></RoleBasedRoute></Suspense>} />
          <Route path="dashboard/principal" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['principal']}><PrincipalDashboard /></RoleBasedRoute></Suspense>} />
          <Route path="dashboard/admin" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['admin']}><AdminDashboard /></RoleBasedRoute></Suspense>} />
          <Route path="dashboard/dean" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['dean']}><DeanDashboard /></RoleBasedRoute></Suspense>} />
          <Route path="dashboard/hr-manager" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['hr_manager']}><HrManagerDashboard /></RoleBasedRoute></Suspense>} />
          <Route path="dashboard/student-mentors" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['student_mentors']}><StudentMentorsDashboard /></RoleBasedRoute></Suspense>} />
          <Route path="dashboard/student-coordinators" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['student_coordinators']}><StudentCoordinatorsDashboard /></RoleBasedRoute></Suspense>} />
          <Route path="dashboard/teachers" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['teachers', 'teacher']}><TeachersDashboard /></RoleBasedRoute></Suspense>} />

          {/* Student routes */}
          <Route path="students">
            <Route index element={<Suspense fallback={<PageLoader />}><StudentList /></Suspense>} />
            <Route path="analytics" element={<Suspense fallback={<PageLoader />}><StudentAnalytics /></Suspense>} />
            <Route path="new" element={<Suspense fallback={<PageLoader />}><StudentForm /></Suspense>} />
            <Route path=":id" element={<Suspense fallback={<PageLoader />}><StudentDetails /></Suspense>} />
            <Route path=":id/edit" element={<Suspense fallback={<PageLoader />}><StudentForm /></Suspense>} />
            <Route path="alumni" element={<Suspense fallback={<PageLoader />}><AlumniList /></Suspense>} />
          </Route>

          {/* Academic Management routes */}
          <Route path="academic-module">
            <Route index element={<Suspense fallback={<PageLoader />}><AcademicModule /></Suspense>} />
          </Route>

          {/* Examination Module routes */}
          <Route path="examination-module">
            <Route index element={<Suspense fallback={<PageLoader />}><ExaminationModule /></Suspense>} />
          </Route>

          {/* Attendance Module routes */}
          <Route path="attendance-module">
            <Route index element={<Suspense fallback={<PageLoader />}><AttendanceModule view="dashboard" /></Suspense>} />
            <Route path="mark" element={<Suspense fallback={<PageLoader />}><AttendanceModule view="mark" /></Suspense>} />
            <Route path="sessions" element={<Suspense fallback={<PageLoader />}><AttendanceModule view="sessions" /></Suspense>} />
            <Route path="leaves" element={<Suspense fallback={<PageLoader />}><AttendanceModule view="leaves" /></Suspense>} />
            <Route path="reports" element={<Suspense fallback={<PageLoader />}><AttendanceModule view="reports" /></Suspense>} />
            <Route path="method-config" element={<Suspense fallback={<PageLoader />}><AttendanceModule view="method-config" /></Suspense>} />
            <Route path="audit-logs" element={<Suspense fallback={<PageLoader />}><AttendanceModule view="audit-logs" /></Suspense>} />
          </Route>

          {/* Attendance routes (legacy) */}
          <Route path="attendance">
            <Route index element={<Suspense fallback={<PageLoader />}><AttendanceList /></Suspense>} />
            <Route path="mark" element={<Suspense fallback={<PageLoader />}><MarkAttendance /></Suspense>} />
            <Route path="subject" element={<Suspense fallback={<PageLoader />}><SubjectAttendance /></Suspense>} />
            <Route path="timetable" element={<Suspense fallback={<PageLoader />}><TimetableAttendance /></Suspense>} />
          </Route>

          {/* Grade routes */}
          <Route path="grades">
            <Route index element={<Suspense fallback={<PageLoader />}><GradeList /></Suspense>} />
            <Route path="new" element={<Suspense fallback={<PageLoader />}><GradeForm /></Suspense>} />
            <Route path=":id/edit" element={<Suspense fallback={<PageLoader />}><GradeForm /></Suspense>} />
          </Route>

          {/* Class routes */}
          <Route path="classes">
            <Route index element={<Suspense fallback={<PageLoader />}><ClassList /></Suspense>} />
            <Route path="new" element={<Suspense fallback={<PageLoader />}><ClassForm /></Suspense>} />
            <Route path=":id" element={<Suspense fallback={<PageLoader />}><ClassDetails /></Suspense>} />
            <Route path=":id/edit" element={<Suspense fallback={<PageLoader />}><ClassForm /></Suspense>} />
          </Route>

          {/* Course routes */}
          <Route path="courses">
            <Route index element={<Suspense fallback={<PageLoader />}><CourseList /></Suspense>} />
            <Route path="new" element={<Suspense fallback={<PageLoader />}><CourseForm /></Suspense>} />
            <Route path=":id" element={<Suspense fallback={<PageLoader />}><CourseDetails /></Suspense>} />
            <Route path=":id/edit" element={<Suspense fallback={<PageLoader />}><CourseForm /></Suspense>} />
          </Route>

          {/* Admission routes */}
          <Route path="admissions">
            <Route index element={<Suspense fallback={<PageLoader />}><AdmissionList /></Suspense>} />
            <Route path="new" element={<Suspense fallback={<PageLoader />}><AdmissionForm /></Suspense>} />
            <Route path=":id" element={<Suspense fallback={<PageLoader />}><AdmissionDetails /></Suspense>} />
            <Route path=":id/edit" element={<Suspense fallback={<PageLoader />}><AdmissionForm /></Suspense>} />
          </Route>

          {/* Admission Criteria routes */}
          <Route path="admission-criteria">
            <Route index element={<Suspense fallback={<PageLoader />}><AdmissionCriteriaList /></Suspense>} />
            <Route path="new" element={<Suspense fallback={<PageLoader />}><AdmissionCriteriaForm /></Suspense>} />
            <Route path=":id/edit" element={<Suspense fallback={<PageLoader />}><AdmissionCriteriaForm /></Suspense>} />
          </Route>

          {/* Finance routes — new enterprise finance module */}
          <Route path="finance">
            <Route index element={<Suspense fallback={<PageLoader />}><FinanceDashboard /></Suspense>} />
            <Route path="chart-of-accounts" element={<Suspense fallback={<PageLoader />}><ChartOfAccounts /></Suspense>} />
            <Route path="general-ledger" element={<Suspense fallback={<PageLoader />}><GeneralLedger /></Suspense>} />
            <Route path="receivables" element={<Suspense fallback={<PageLoader />}><Receivables /></Suspense>} />
            <Route path="payables" element={<Suspense fallback={<PageLoader />}><Payables /></Suspense>} />
            <Route path="cash-bank" element={<Suspense fallback={<PageLoader />}><CashBank /></Suspense>} />
            <Route path="budgets" element={<Suspense fallback={<PageLoader />}><Budgets /></Suspense>} />
            <Route path="expenses" element={<Suspense fallback={<PageLoader />}><Expenses /></Suspense>} />
            <Route path="income" element={<Suspense fallback={<PageLoader />}><Income /></Suspense>} />
            <Route path="statements" element={<Suspense fallback={<PageLoader />}><Statements /></Suspense>} />
            <Route path="reports" element={<Suspense fallback={<PageLoader />}><Reports /></Suspense>} />
            <Route path="approvals" element={<Suspense fallback={<PageLoader />}><Approvals /></Suspense>} />
            <Route path="tax" element={<Suspense fallback={<PageLoader />}><TaxCompliance /></Suspense>} />
            <Route path="audit" element={<Suspense fallback={<PageLoader />}><AuditLogs /></Suspense>} />
            <Route path="ai" element={<Suspense fallback={<PageLoader />}><AIInsights /></Suspense>} />
            <Route path="integrations" element={<Suspense fallback={<PageLoader />}><Integrations /></Suspense>} />
            <Route path="procurement" element={<Suspense fallback={<PageLoader />}><Procurement /></Suspense>} />
            <Route path="fixed-assets" element={<Suspense fallback={<PageLoader />}><FixedAssets /></Suspense>} />
          </Route>

          {/* Fee Management route */}
          <Route path="fees" element={<Suspense fallback={<PageLoader />}><FeeManagement /></Suspense>} />

          {/* Hostel route */}
          <Route path="hostel" element={<Suspense fallback={<PageLoader />}><HostelManagement /></Suspense>} />

          {/* Cafeteria route */}
          <Route path="cafeteria" element={<Suspense fallback={<PageLoader />}><CafeteriaManagement /></Suspense>} />

          {/* Houses & Clubs route */}
          <Route path="houses-clubs" element={<Suspense fallback={<PageLoader />}><HousesClubs /></Suspense>} />

          {/* Payroll route */}
          <Route path="payroll" element={<Suspense fallback={<PageLoader />}><PayrollManagement /></Suspense>} />

          {/* ICSE Exam route */}
          <Route path="exam-icse" element={<Suspense fallback={<PageLoader />}><ExamICSE /></Suspense>} />

          {/* Communication route */}
          <Route path="communication-center" element={<Suspense fallback={<PageLoader />}><CommunicationManagement /></Suspense>} />

          {/* Inventory & Store route */}
          <Route path="inventory" element={<Suspense fallback={<PageLoader />}><InventoryStore /></Suspense>} />

          {/* Management Dashboard route */}
          <Route path="management-dashboard" element={<Suspense fallback={<PageLoader />}><ManagementDashboard /></Suspense>} />

          {/* Reports Module route */}
          <Route path="reports" element={<Suspense fallback={<PageLoader />}><ReportsModule /></Suspense>} />

          {/* Transport routes */}
          <Route path="transport">
            <Route index element={<Suspense fallback={<PageLoader />}><TransportManagement /></Suspense>} />
            <Route path="tracking" element={<Suspense fallback={<PageLoader />}><VehicleTracking /></Suspense>} />
            <Route path="reports" element={<Suspense fallback={<PageLoader />}><TransportReports /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={<PageLoader />}><TrackingSettings /></Suspense>} />
          </Route>

          {/* Staff routes */}
          <Route path="staff">
            <Route index element={<Suspense fallback={<PageLoader />}><StaffManagement /></Suspense>} />
            <Route path="biometric" element={<Suspense fallback={<PageLoader />}><BiometricEnrollment /></Suspense>} />
          </Route>

          {/* HR routes */}
          <Route path="hr">
            <Route index element={<Suspense fallback={<PageLoader />}><HRDashboard /></Suspense>} />
            <Route path="module" element={<Suspense fallback={<PageLoader />}><HRModule /></Suspense>} />
            <Route path="onboarding" element={<Suspense fallback={<PageLoader />}><OnboardingManagement /></Suspense>} />
            <Route path="offboarding" element={<Suspense fallback={<PageLoader />}><OffboardingManagement /></Suspense>} />
            <Route path="interviews" element={<Suspense fallback={<PageLoader />}><InterviewManagement /></Suspense>} />
            <Route path="leaves" element={<Suspense fallback={<PageLoader />}><LeaveManagement /></Suspense>} />
            <Route path="self-service" element={<Suspense fallback={<PageLoader />}><SelfServicePortal /></Suspense>} />
            <Route path="analytics" element={<Suspense fallback={<PageLoader />}><HRAnalyticsDashboard /></Suspense>} />
          </Route>

          {/* Asset Management routes */}
          <Route path="assets">
            <Route index element={<Suspense fallback={<PageLoader />}><AssetManagement /></Suspense>} />
            <Route path="list" element={<Suspense fallback={<PageLoader />}><AssetList /></Suspense>} />
            <Route path="categories" element={<Suspense fallback={<PageLoader />}><AssetCategories /></Suspense>} />
            <Route path="maintenance" element={<Suspense fallback={<PageLoader />}><AssetMaintenance /></Suspense>} />
            <Route path="transfers" element={<Suspense fallback={<PageLoader />}><AssetTransfers /></Suspense>} />
            <Route path="disposals" element={<Suspense fallback={<PageLoader />}><AssetDisposals /></Suspense>} />
            <Route path="requests" element={<Suspense fallback={<PageLoader />}><StaffAssetRequests /></Suspense>} />
            <Route path="new" element={<Suspense fallback={<PageLoader />}><AssetForm /></Suspense>} />
            <Route path=":id" element={<Suspense fallback={<PageLoader />}><AssetDetails /></Suspense>} />
            <Route path=":id/edit" element={<Suspense fallback={<PageLoader />}><AssetForm /></Suspense>} />
          </Route>

          {/* Library Management routes */}
          <Route path="library">
            <Route index element={<Suspense fallback={<PageLoader />}><LibraryManagement /></Suspense>} />
            <Route path="books" element={<Suspense fallback={<PageLoader />}><Books /></Suspense>} />
            <Route path="books/new" element={<Suspense fallback={<PageLoader />}><BookForm /></Suspense>} />
            <Route path="books/:id" element={<Suspense fallback={<PageLoader />}><BookForm /></Suspense>} />
            <Route path="books/:id/edit" element={<Suspense fallback={<PageLoader />}><BookForm /></Suspense>} />
            <Route path="members" element={<Suspense fallback={<PageLoader />}><LibraryMembers /></Suspense>} />
            <Route path="members/new" element={<Suspense fallback={<PageLoader />}><MemberForm /></Suspense>} />
            <Route path="members/:id" element={<Suspense fallback={<PageLoader />}><MemberForm /></Suspense>} />
            <Route path="members/:id/edit" element={<Suspense fallback={<PageLoader />}><MemberForm /></Suspense>} />
            <Route path="issues" element={<Suspense fallback={<PageLoader />}><BookIssues /></Suspense>} />
            <Route path="reservations" element={<Suspense fallback={<PageLoader />}><BookReservations /></Suspense>} />
            <Route path="fines" element={<Suspense fallback={<PageLoader />}><Fines /></Suspense>} />
          </Route>

          {/* Front Office Management routes */}
          <Route path="front-office">
            <Route index element={<Suspense fallback={<PageLoader />}><FrontOfficeManagement /></Suspense>} />
            <Route path="visitors" element={<Suspense fallback={<PageLoader />}><Visitors /></Suspense>} />
            <Route path="appointments" element={<Suspense fallback={<PageLoader />}><Appointments /></Suspense>} />
            <Route path="inquiries" element={<Suspense fallback={<PageLoader />}><Inquiries /></Suspense>} />
            <Route path="phone-calls" element={<Suspense fallback={<PageLoader />}><PhoneCalls /></Suspense>} />
            <Route path="mail-packages" element={<Suspense fallback={<PageLoader />}><MailPackages /></Suspense>} />
          </Route>

          {/* Facility Management routes */}
          <Route path="facility">
            <Route index element={<Suspense fallback={<PageLoader />}><FacilityManagement /></Suspense>} />
            <Route path="facilities" element={<Suspense fallback={<PageLoader />}><Facilities /></Suspense>} />
            <Route path="bookings" element={<Suspense fallback={<PageLoader />}><FacilityBookings /></Suspense>} />
            <Route path="maintenance" element={<Suspense fallback={<PageLoader />}><FacilityMaintenance /></Suspense>} />
            <Route path="inventory" element={<Suspense fallback={<PageLoader />}><FacilityInventory /></Suspense>} />
            <Route path="inspections" element={<Suspense fallback={<PageLoader />}><FacilityInspections /></Suspense>} />
          </Route>

          {/* Board & Affiliation routes */}
          <Route path="boards" element={<Suspense fallback={<PageLoader />}><BoardManagement /></Suspense>} />
          <Route path="affiliations" element={<Suspense fallback={<PageLoader />}><AffiliationManagement /></Suspense>} />
          <Route path="boards/scholarships" element={<Suspense fallback={<PageLoader />}><Scholarships /></Suspense>} />
          <Route path="boards/exam-schedules" element={<Suspense fallback={<PageLoader />}><ExamSchedules /></Suspense>} />
          <Route path="boards/grading-scales" element={<Suspense fallback={<PageLoader />}><GradingScales /></Suspense>} />
          <Route path="boards/service-books" element={<Suspense fallback={<PageLoader />}><ServiceBooks /></Suspense>} />
          <Route path="boards/rte-reimbursements" element={<Suspense fallback={<PageLoader />}><RTEReimbursements /></Suspense>} />
          <Route path="boards/board-exam-registrations" element={<Suspense fallback={<PageLoader />}><BoardExamRegistrations /></Suspense>} />
          <Route path="boards/elective-choices" element={<Suspense fallback={<PageLoader />}><ElectiveChoices /></Suspense>} />
          <Route path="boards/subject-groups" element={<Suspense fallback={<PageLoader />}><SubjectGroups /></Suspense>} />
          <Route path="boards/udise-exports" element={<Suspense fallback={<PageLoader />}><UDISEExports /></Suspense>} />

          {/* Old academic sub-routes removed — consolidated into /academic-module */}

          {/* School Master Setup route */}
          <Route path="school-setup" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['admin', 'super_admin', 'principal']}><SchoolMasterSetup /></RoleBasedRoute></Suspense>} />

          {/* User Management route */}
          <Route path="user-management" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['admin', 'super_admin']}><UserManagementUnified /></RoleBasedRoute></Suspense>} />

          {/* Admission Module route */}
          <Route path="admission-module" element={<Suspense fallback={<PageLoader />}><AdmissionModule /></Suspense>} />
          <Route path="admission-workflow" element={<Suspense fallback={<PageLoader />}><AdmissionWorkflow /></Suspense>} />

          {/* Admin routes */}
          <Route path="users" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['admin', 'super_admin']}><UserManagement /></RoleBasedRoute></Suspense>} />
          <Route path="user-roles" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['admin', 'super_admin']}><UserRoles /></RoleBasedRoute></Suspense>} />
          <Route path="branches" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['admin', 'super_admin']}><BranchManagement /></RoleBasedRoute></Suspense>} />
          <Route path="school-profile" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['admin', 'super_admin']}><SchoolProfilePage /></RoleBasedRoute></Suspense>} />
          <Route path="settings" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['admin', 'super_admin']}><Settings /></RoleBasedRoute></Suspense>} />
          <Route path="audit-logs" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['admin', 'super_admin']}><AdminAuditLogs /></RoleBasedRoute></Suspense>} />
          <Route path="system-health" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={['admin', 'super_admin']}><SystemHealth /></RoleBasedRoute></Suspense>} />

          {/* Profile routes */}
          <Route path="profile" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={[]}><Profile /></RoleBasedRoute></Suspense>} />

          {/* Notifications */}
          <Route path="notifications" element={<Suspense fallback={<PageLoader />}><RoleBasedRoute allowedRoles={[]}><Notifications /></RoleBasedRoute></Suspense>} />
        </Route>

        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}

export default App;
